import type { ReactNode } from 'react';
import './Badge.css';

export type BadgeVariant = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export interface BadgeProps {
  variant?: BadgeVariant;
  /** Optional leading icon. Per a11y.md §2 ("never colour-only"), an icon
   * paired with the required text `children` is how this component satisfies
   * that rule — a Badge with only an icon and no text is a misuse of this
   * component, not a supported configuration. */
  icon?: ReactNode;
  /**
   * Render a leading status dot — the prototype's mode badge, source chips and
   * unread markers all use one. Like `icon` it is decorative: it inherits the
   * variant's own colour and is `aria-hidden`, so it never becomes the only
   * carrier of meaning. The text still has to say what the state is.
   */
  dot?: boolean;
  /**
   * A numeric badge: monospaced tabular figures, and no uppercasing (there is
   * nothing to uppercase in "12"). The prototype makes exactly this split —
   * mono for every count, sans for every word — and it is what keeps a column
   * of nav counts aligned.
   */
  count?: boolean;
  children: ReactNode;
}

export function Badge({ variant = 'neutral', icon, dot, count, children }: BadgeProps) {
  const className = ['dpr-badge', `dpr-badge--${variant}`, count ? 'dpr-badge--count' : null]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={className}>
      {dot ? <span className="dpr-badge__dot" aria-hidden="true" /> : null}
      {icon ? (
        <span className="dpr-badge__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}
